import { defineStore } from 'pinia';
import { ref } from 'vue';
import Client from '@/lib/client';
import { 
  mockSeriesOverview, 
  mockSeriesDetail, 
  mockReportOverview, 
  mockGatewayDetail 
} from '@/mocks/api-data';

// Initialize API client
const apiClient = new Client('web', 'v2');

// Request controllers for cancellation
let reportsAbortController: AbortController | null = null;
let filtersAbortController: AbortController | null = null;

// API Endpoints
const API_ENDPOINTS = {
  reports: 'reports',
  filters: 'reports/filters',
  reportDetail: (slug: string) => `reports/${slug}`,
  reportGrade: (slug: string, grade: string) => `reports/${slug}/${grade}`,
  reportGateway: (slug: string, grade: string, gateway: string) => 
    `reports/${slug}/${grade}/${gateway}`,
};

export type Report = {
  slug: string;
  title: string;
  cover: {
    uri: string;
    name: string;
    extension: string;
    mime_type: string;
    alt: string;
    width: number;
    height: number;
  };
  subject: {
    slug: string;
    name: string;
    title: string;
  };
  format: {
    slug: string;
    name: string;
    type: string;
    subtype: string;
    icon: {
      uri: string;
      name: string;
      extension: string;
      mime_type: string;
      alt: string;
      width: number;
      height: number;
    };
  };
  grades: Array<{
    slug: string;
    name: string;
    title: string;
    order: number;
  }>;
  release: number;
  version: string;
  gateways: Array<{
    slug: string;
    label: string;
    name: string;
    title: string;
    score: string;
    status: {
      slug: string;
      name: string;
      title: string;
    };
    order: number;
    failed_non_negotiable: boolean;
  }>;
  details: Array<{
    label: string;
    value: string;
  }>;
  groups: string[];
  alerts: Array<{
    class: string;
    content: string;
  }>;
  copyright: string;
};

interface ApiResponse<T> {
  items?: T[];
  data?: T;
  total?: number;
}

// Add filter types
export interface FilterOption {
  value: string;
  label: string;
  name?: string;
  disabled?: boolean;
}

export interface FilterGroup {
  id: string;
  name: string;
  options: FilterOption[];
  slug?: string;
  label?: string;
  value?: string;
  multiple?: boolean;
  sequential?: boolean;
  required?: boolean;
  disabled?: boolean;
  description?: string;
}

// Define store
export const useReportsStore = defineStore('reports', {
  state: () => ({
    reports: [] as Report[],
    loading: false,
    error: null as string | null,
    sortOption: 'published',
    selectedSubjects: [] as string[],
    selectedFormats: [] as string[],
    selectedGrades: [] as string[],
    selectedStandards: [] as string[],
    // Pagination state
    currentPage: 1,
    itemsPerPage: 12,
    totalItems: 0,
    filters: [] as FilterGroup[],
    gradeRange: { start: 0, end: 12 } as { start: number; end: number },
    // Request tracking to prevent duplicate calls
    reportsRequestInProgress: false,
    filtersRequestInProgress: false,
    lastFetchTimestamp: 0,
    minFetchInterval: 300, // Minimum ms between identical fetch requests
    requestCount: 0, // Track total requests for debugging
  }),
  
  actions: {
    // Initialize the store
    async initialize() {
      try {
        this.loading = true;
        
        if (!import.meta.env.VITE_APP_API_URL) {
          throw new Error('API URL not configured. Please set VITE_APP_API_URL in your environment.');
        }
        
        // First fetch filters
        await this.fetchFilters();

        // Then load reports
        await this.fetchReports();
      } catch (error) {
        console.error('Error initializing store:', error);
        this.error = error instanceof Error ? error.message : 'Failed to initialize store';
      } finally {
        this.loading = false;
      }
    },

    async fetchReports() {
      // Cancel any existing request
      if (reportsAbortController) {
        reportsAbortController.abort();
        reportsAbortController = null;
      }

      // Create a new abort controller for this request
      reportsAbortController = new AbortController();

      // Prevent duplicate calls
      if (this.reportsRequestInProgress) {
        return;
      }

      // Throttle identical requests but don't block initial fetch
      const now = Date.now();
      if (now - this.lastFetchTimestamp < this.minFetchInterval && this.reports.length > 0) {
        return;
      }

      this.reportsRequestInProgress = true;
      this.loading = true;
      this.error = null;
      this.lastFetchTimestamp = now;
      this.requestCount++;

      try {
        // Map sort options to API parameters
        const sortParams = {
          'Recently Published': { order: 'published', direction: 'desc' },
          'Alphabetical': { order: 'alpha', direction: 'asc' },
          'Copyright': { order: 'edition', direction: 'asc' }
        }[this.sortOption];

        // Use real API with Client class
        const requestBody = {
          filters: {
            subject: this.selectedSubjects.length > 0 ? this.selectedSubjects : [],
            format: this.selectedFormats.length > 0 ? this.selectedFormats : [],
            grade: this.selectedGrades.length > 0 ? this.selectedGrades : []
          },
          limit: this.itemsPerPage,
          offset: (this.currentPage - 1) * this.itemsPerPage,
          ...sortParams
        };
        
        // Add signal to request to enable cancellation
        const options = {
          signal: reportsAbortController.signal,
          timeout: 30000 // Add a 30 second timeout
        };

        // Add retry logic for failed requests
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            // Directly use the apiClient which constructs the URL correctly
            const response = await apiClient.execute(
              API_ENDPOINTS.reports, 
              requestBody, 
              'POST',
              options
            ) as ApiResponse<Report>;
            
            // Process response data
            this.reports = response.items || [];
            this.totalItems = response.total || 0;
            break; // Success, exit retry loop
          } catch (err) {
            retryCount++;
            
            // Don't retry aborted requests
            if (err && (err as any).name === 'AbortError') {
              return;
            }
            
            // If we've exhausted retries, throw the error
            if (retryCount === maxRetries) {
              throw err;
            }
            
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          }
        }
      } catch (err) {
        // Don't record aborted requests as errors
        if (err && (err as any).name === 'AbortError') {
          return;
        }
        
        this.error = err instanceof Error ? err.message : 'Failed to fetch reports';
        console.error('Error in fetchReports:', err);
      } finally {
        this.reportsRequestInProgress = false;
        this.loading = false;
        reportsAbortController = null;
      }
    },

    // Set the current page and fetch reports
    setPage(page: number) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.fetchReports(); // Fetch reports for the new page
    },

    // Set items per page and fetch reports
    setItemsPerPage(items: number) {
      if (items === this.itemsPerPage) return;
      this.itemsPerPage = items;
      this.currentPage = 1; // Reset to first page when changing items per page
      this.fetchReports();
    },

    async fetchReportDetail(slug: string) {
      this.loading = true;
      this.error = null;
      try {
        // Use real API with Client class
        const response = await apiClient.execute(API_ENDPOINTS.reportDetail(slug)) as ApiResponse<Report>;
        return response.data;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch report detail';
        console.error('Error in fetchReportDetail:', err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchReportGrade(slug: string, grade: string) {
      this.loading = true;
      this.error = null;
      try {
        // Use real API with Client class
        const response = await apiClient.execute(API_ENDPOINTS.reportGrade(slug, grade)) as ApiResponse<Report>;
        return response.data;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch report grade';
        console.error('Error in fetchReportGrade:', err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchReportGateway(slug: string, grade: string, gateway: string) {
      this.loading = true;
      this.error = null;
      try {
        // Use real API with Client class
        const response = await apiClient.execute(API_ENDPOINTS.reportGateway(slug, grade, gateway)) as ApiResponse<Report>;
        return response.data;
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Failed to fetch report gateway';
        console.error('Error in fetchReportGateway:', err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    setSortOption(option: string) {
      if (this.sortOption === option) return; // Avoid redundant state updates
      this.sortOption = option;
      this.currentPage = 1; // Reset to first page when sort changes
      // REMOVED: this.fetchReports(); // Fetch is now triggered by router guard
    },

    toggleSubject(subject: string) {
      const index = this.selectedSubjects.indexOf(subject);
      if (index === -1) {
        this.selectedSubjects.push(subject);
      } else {
        this.selectedSubjects.splice(index, 1);
      }
    },

    setSubject(subject: string, selected: boolean) {
      const upperCaseSubject = subject.toUpperCase();
      const currentIndex = this.selectedSubjects.findIndex(subj => subj.toUpperCase() === upperCaseSubject);
      let stateChanged = false;

      if (selected) {
        if (currentIndex === -1 || this.selectedSubjects.length > 1) {
          this.selectedSubjects = [subject]; 
          stateChanged = true;
        }
      } else {
        if (currentIndex !== -1) {
          // Only allow deselecting if it's not the only one selected
          if (this.selectedSubjects.length > 1) {
            this.selectedSubjects.splice(currentIndex, 1);
            stateChanged = true;
          } 
          // If trying to deselect the only subject, state doesn't change
        }
      }
      
      if (stateChanged) {
        this.currentPage = 1; // Reset page only if subject actually changed
        // REMOVED: this.fetchReports(); // Fetch is now triggered by router guard
      }
    },

    setFormat(format: string, selected: boolean) {
      if (selected) {
        if (!this.selectedFormats.includes(format)) {
          this.selectedFormats.push(format);
        }
      } else {
        const index = this.selectedFormats.indexOf(format);
        if (index !== -1) {
          this.selectedFormats.splice(index, 1);
        }
      }
    },

    setGrade(grade: string, selected: boolean) {
      if (selected) {
        if (!this.selectedGrades.includes(grade)) {
          this.selectedGrades.push(grade);
        }
      } else {
        const index = this.selectedGrades.indexOf(grade);
        if (index !== -1) {
          this.selectedGrades.splice(index, 1);
        }
      }
    },

    setStandard(standard: string, selected: boolean) {
      if (selected) {
        if (!this.selectedStandards.includes(standard)) {
          this.selectedStandards.push(standard);
        }
      } else {
        const index = this.selectedStandards.indexOf(standard);
        if (index !== -1) {
          this.selectedStandards.splice(index, 1);
        }
      }
    },

    async fetchFilters() {
      // Cancel any existing request
      if (filtersAbortController) {
        filtersAbortController.abort();
        filtersAbortController = null;
      }

      // Create a new abort controller for this request
      filtersAbortController = new AbortController();

      // Prevent duplicate calls
      if (this.filtersRequestInProgress) {
        return;
      }

      // If we already have filters, don't fetch again unless forced
      if (this.filters && this.filters.length > 0) {
        return;
      }

      this.filtersRequestInProgress = true;
      this.loading = true;
      this.error = null;
      this.requestCount++;
      
      try {
        // Add signal to request to enable cancellation
        const options = {
          signal: filtersAbortController.signal
        };
        
        // Use real API with Client class
        const response = await apiClient.execute(
          API_ENDPOINTS.filters, 
          undefined, 
          'GET', 
          options
        ) as ApiResponse<FilterGroup>;
        
        // Process the API response to ensure proper structure
        if (response.items) {
          this.filters = response.items.map(filter => ({
            id: filter.slug || filter.id,
            name: filter.label || filter.name || this.getFilterNameFromId(filter.slug || filter.id),
            options: filter.options.map(option => ({
              value: option.value,
              label: option.label || option.name || option.value
            }))
          }));
        } else {
          this.filters = [];
        }
      } catch (err) {
        // Don't record aborted requests as errors
        if (err && (err as any).name === 'AbortError') {
          return;
        }
        
        this.error = err instanceof Error ? err.message : 'Failed to fetch filters';
        console.error('Error in fetchFilters:', err);
      } finally {
        this.filtersRequestInProgress = false;
        this.loading = false;
        filtersAbortController = null;
      }
    },

    // Helper function to get human-readable filter names
    getFilterNameFromId(id: string): string {
      const filterNames: Record<string, string> = {
        'subject': 'Subject',
        'format': 'Format',
        'grade': 'Grade Level',
        'alignment': 'Alignment Rating',
        'usability': 'Usability Rating',
        'techInfo': 'Technology Information'
      };
      return filterNames[id] || id;
    },

    setGradeRange(start: number, end: number) {
      this.gradeRange = { start, end };
    },

    setSelectedFilters(filters: { subjects?: string[], formats?: string[], grades?: string[] }) {
      if (filters.subjects) {
        this.selectedSubjects = filters.subjects;
      }
      if (filters.formats) {
        this.selectedFormats = filters.formats;
      }
      if (filters.grades) {
        this.selectedGrades = filters.grades;
      }
      this.fetchReports();
    }
  },

  getters: {
    filteredReports: (state) => {
      let filtered = [...state.reports];

      // Apply subject filter
      if (state.selectedSubjects.length > 0) {
        filtered = filtered.filter(report => 
          state.selectedSubjects.includes(report.subject.name)
        );
      }

      // Apply format filter
      if (state.selectedFormats.length > 0) {
        filtered = filtered.filter(report =>
          state.selectedFormats.includes(report.format.name)
        );
      }

      // Apply grade filter
      if (state.selectedGrades.length > 0) {
        filtered = filtered.filter(report => {
          // Skip reports with no grades
          if (!report.grades || !report.grades.length) return false;

          // Check if any of the report's grades match the selected grades
          return report.grades.some(grade => 
            state.selectedGrades.includes(grade.name)
          );
        });
      }

      return filtered;
    }
  }
});
